import React from 'react';

import { Tooltip } from 'antd-v5';
import { MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import { RenewalStatus } from '@appTypes/enums';
import type { RosterScout } from '@modules/advancement/duck/types';
import { getIsDenChief } from '@modules/advancement/packRoster/utilsTyped';
import { isInactiveUnitSel } from '@modules/unit/duck/selectorsTyped';
import { ConditionalRender, S, Spin } from '@shared/components';
import { ProgramId } from '@shared/constants';

import styles from './Columns.scss';

const MAX_AGE_PER_PROGRAM: Record<number, number> = {
  [ProgramId.BOY_SCOUT]: 18,
  [ProgramId.VENTURING]: 21,
  [ProgramId.SEA_SCOUT]: 21,
};

export const RenewalStatusColumn: React.FC<{
  renewalStatus: string;
  record: RosterScout;
  areRenewalsLoading?: boolean;
  orgIsPack?: boolean;
  unitTypeId?: number;
}> = props => {
  const { areRenewalsLoading, record, renewalStatus, orgIsPack, unitTypeId } =
    props;
  const { registrationId } = record;
  const isInactiveUnit = useSelector(isInactiveUnitSel);
  const maxAge = unitTypeId ? MAX_AGE_PER_PROGRAM[unitTypeId] : 18;
  if (areRenewalsLoading && !registrationId) {
    return <Spin size="small" />;
  }

  const isDenChief = getIsDenChief(record);

  if (isDenChief && orgIsPack) {
    return <FormattedMessage id="shared.na" />;
  }

  if (isInactiveUnit) {
    return (
      <FormattedMessage id="advancement.PackRoster.column.renewalStatus.expiredUnit" />
    );
  }

  const showInfo =
    (renewalStatus === RenewalStatus.Expired ||
      renewalStatus === RenewalStatus.Eligible) &&
    !!record.bsaOrderId;

  return (
    <Text inline>
      {renewalStatus}{' '}
      <ConditionalRender value={Number(record.age) >= maxAge}>
        <S>
          <FormattedMessage
            id="advancement.PackRoster.column.renewalStatus.overAge"
            values={{ maxAge: maxAge }}
          />
        </S>
      </ConditionalRender>
      <ConditionalRender value={showInfo}>
        <Tooltip
          title={
            <Text color="color-white" inline font="body-caption">
              <FormattedMessage id="advancement.PackRoster.column.renewalStatus.tooltip" />
            </Text>
          }
        >
          <MdInfo className={styles.infoIcon} />
        </Tooltip>
      </ConditionalRender>
    </Text>
  );
};
